/** Normalised per-frame input snapshot. */
export interface InputState {
  /** Normalised horizontal movement (-1..0..1). */
  dx: number;
  /** Normalised vertical movement (-1..0..1). */
  dy: number;
  /** Aim angle in radians (atan2 from screen centre). */
  aimAngle: number;
  /** True while primary mouse button is held. */
  shooting: boolean;
  /** True on the frame the melee key (F or Space) was first pressed. */
  melee: boolean;
}

/**
 * InputHandler tracks WASD keyboard + mouse for the game loop.
 *
 * Usage:
 *   const input = new InputHandler(app.canvas);
 *   // In game loop:
 *   const state = input.getState();
 *   // At cleanup:
 *   input.destroy();
 */
export class InputHandler {
  private keys      = new Set<string>();
  private mouseX    = 0;
  private mouseY    = 0;
  private shooting  = false;
  private meleePending = false;

  // Bound listeners (kept for removeEventListener)
  private _onKeyDown: (e: KeyboardEvent) => void;
  private _onKeyUp:   (e: KeyboardEvent) => void;
  private _onMouseMove: (e: MouseEvent) => void;
  private _onMouseDown: (e: MouseEvent) => void;
  private _onMouseUp:   (e: MouseEvent) => void;

  constructor(private canvas: HTMLCanvasElement) {
    this._onKeyDown   = (e) => this.onKeyDown(e);
    this._onKeyUp     = (e) => this.onKeyUp(e);
    this._onMouseMove = (e) => this.onMouseMove(e);
    this._onMouseDown = (e) => this.onMouseDown(e);
    this._onMouseUp   = (e) => this.onMouseUp(e);

    window.addEventListener('keydown',    this._onKeyDown);
    window.addEventListener('keyup',      this._onKeyUp);
    canvas.addEventListener('mousemove',  this._onMouseMove);
    canvas.addEventListener('mousedown',  this._onMouseDown);
    canvas.addEventListener('mouseup',    this._onMouseUp);
    // Prevent right-click context menu in-game
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // ── Private event handlers ───────────────────────────────────────────────

  private onKeyDown(e: KeyboardEvent): void {
    this.keys.add(e.code);
    // Melee: F key or Space
    if (e.code === 'KeyF' || e.code === 'Space') {
      this.meleePending = true;
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.keys.delete(e.code);
  }

  private onMouseMove(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.mouseX = e.clientX - rect.left;
    this.mouseY = e.clientY - rect.top;
  }

  private onMouseDown(e: MouseEvent): void {
    if (e.button === 0) this.shooting = true;
  }

  private onMouseUp(e: MouseEvent): void {
    if (e.button === 0) this.shooting = false;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Returns the current input snapshot.
   * The `melee` flag is automatically cleared after being read once (edge-trigger).
   */
  getState(): InputState {
    const right  = this.keys.has('KeyD') || this.keys.has('ArrowRight') ? 1 : 0;
    const left   = this.keys.has('KeyA') || this.keys.has('ArrowLeft')  ? 1 : 0;
    const down   = this.keys.has('KeyS') || this.keys.has('ArrowDown')  ? 1 : 0;
    const up     = this.keys.has('KeyW') || this.keys.has('ArrowUp')    ? 1 : 0;

    const rawDx = right - left;
    const rawDy = down  - up;
    const mag   = Math.hypot(rawDx, rawDy) || 1;

    // Aim angle is relative to canvas centre
    const cx = this.canvas.width  / 2;
    const cy = this.canvas.height / 2;
    const aimAngle = Math.atan2(this.mouseY - cy, this.mouseX - cx);

    const melee = this.meleePending;
    this.meleePending = false; // consume the edge

    return {
      dx:       rawDx === 0 && rawDy === 0 ? 0 : rawDx / mag,
      dy:       rawDx === 0 && rawDy === 0 ? 0 : rawDy / mag,
      aimAngle,
      shooting: this.shooting,
      melee,
    };
  }

  /** Whether any movement key is currently pressed. */
  isMoving(): boolean {
    return (
      this.keys.has('KeyW') || this.keys.has('KeyA') ||
      this.keys.has('KeyS') || this.keys.has('KeyD') ||
      this.keys.has('ArrowUp') || this.keys.has('ArrowLeft') ||
      this.keys.has('ArrowDown') || this.keys.has('ArrowRight')
    );
  }

  /** Remove all DOM listeners. Call on screen teardown. */
  destroy(): void {
    window.removeEventListener('keydown',   this._onKeyDown);
    window.removeEventListener('keyup',     this._onKeyUp);
    this.canvas.removeEventListener('mousemove',  this._onMouseMove);
    this.canvas.removeEventListener('mousedown',  this._onMouseDown);
    this.canvas.removeEventListener('mouseup',    this._onMouseUp);
  }
}
